/*
 * @Author: Cozy
 * @Date: 2024-05-27 13:12:56
 * @LastEditors: Cozy
 * @LastEditTime: 2024-07-10 19:39:33
 * @FilePath: /SChatUI/src/AuthContext.js
 * @Description: 
 * 
 * Copyright (c) 2024 by Cozy, All Rights Reserved. 
 */

// AuthContext.js
import React, { createContext, useReducer, useEffect } from 'react';

export const AuthContext = createContext();

const initialState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  expiresIn: null,
  refreshAttempts: 0,
  loading: true,
  accessibleServices: [],  // 添加可访问服务信息
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_AUTH':
      return {
        ...state,
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
        user: action.payload.user,
        expiresIn: action.payload.expiresIn,
        refreshAttempts: 0,
        loading: false,
        accessibleServices: action.payload.accessibleServices,  // 更新可访问服务信息
      };
    case 'CLEAR_AUTH':
      return {
        ...state,
        accessToken: null,
        refreshToken: null,
        user: null,
        expiresIn: null,
        refreshAttempts: 0,
        loading: false,
        accessibleServices: [],  // 清空可访问服务信息
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'INCREMENT_REFRESH_ATTEMPTS':
      return {
        ...state,
        refreshAttempts: state.refreshAttempts + 1,
      };
    default:
      return state;
  }
};
const loadAuthFromStorage = () => {
  return {
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken'),
    user: localStorage.getItem('user'),
    expiresIn: parseInt(localStorage.getItem('expiresIn'), 10),
    accessibleServices: JSON.parse(localStorage.getItem('accessibleServices')) || [],  // 从本地存储加载可访问服务信息
  };
};

const saveAuthToStorage = (auth) => {
  localStorage.setItem('accessToken', auth.accessToken);
  localStorage.setItem('refreshToken', auth.refreshToken);
  localStorage.setItem('user', auth.user);
  localStorage.setItem('expiresIn', auth.expiresIn);
  localStorage.setItem('accessibleServices', JSON.stringify(auth.accessibleServices));  // 保存可访问服务信息
};

const clearAuthFromStorage = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('expiresIn');
  localStorage.removeItem('accessibleServices');  // 移除可访问服务信息
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const auth = loadAuthFromStorage();
    if (auth.accessToken) {
      dispatch({ type: 'SET_AUTH', payload: auth });
      console.debug('[loadAuthFromStorage]:', auth);
    } else {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  useEffect(() => {
    const refreshAccessTokenWithRetry = async () => {
      try {
        console.debug('refresh token');
        await refreshAccessToken();
      } catch (error) {
        console.error('Failed to refresh access token:', error);
        if (state.refreshAttempts >= 3 || error.message === 'Invalid refresh token') {
          clearAuth();
          alert('Session expired. Please log in again.');
          window.location.href = '/login'; // 跳转到登录页面
        } else {
          dispatch({ type: 'INCREMENT_REFRESH_ATTEMPTS' });
        }
      }
    };

    const refreshAccessToken = async () => {
      try {
        if (!state.refreshToken || !state.user) {
          throw new Error('No refresh token or user found');
        }
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${state.refreshToken}`
          },
          body: JSON.stringify({})
        });
        if (response.ok) {
          const responseData = await response.json();
          const accessToken = responseData.access_token;
          const refreshToken = responseData.refresh_token;
          const user = state.user;
          const expiresIn = responseData.expires_in * 1000; // 转换为毫秒
          console.info('Token updated');
          updateAuth(accessToken, refreshToken, user, expiresIn, state.accessibleServices);  // 保留当前的可访问服务信息
        } else {
          console.error('[refreshAccessToken] Failed:', response);
          if (response.status === 401) { // 处理刷新token无效的情况
            throw new Error('Invalid refresh token');
          } else {
            console.error('[refreshAccessToken] 401 el')
          }
          throw new Error('Failed to refresh token');
        }
      } catch (error) {
        console.error("Failed to refresh access token:", error);
        throw error;
      }
    };
    const interval = setInterval(() => {
      refreshAccessTokenWithRetry();
    }, 600000); // 10分钟
    return () => clearInterval(interval);
  }, [state.accessToken]);
  const updateAuth = (accessToken, refreshToken, user, expiresIn, accessibleServices) => {
    const auth = { accessToken, refreshToken, user, expiresIn, accessibleServices };
    saveAuthToStorage(auth);
    dispatch({ type: 'SET_AUTH', payload: auth });
  };

  const clearAuth = () => {
    clearAuthFromStorage();
    dispatch({ type: 'CLEAR_AUTH' });
  };

  return (
    <AuthContext.Provider value={{ ...state, updateAuth, clearAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
